// The brand's coverage map, minus Google. Everything here is a pure function of
// the screens the backend returned, so the status buttons, their counts, the pins
// and the city rollup are all derived from one payload and cannot disagree.

import type {
  CampaignStatus,
  MapCampaign,
  MapScreen,
} from "../schemas/campaignSchema";

/** The three buttons. "Live" is a campaign inside its window, not "on air now". */
export const COVERAGE_STATUSES: {
  status: CampaignStatus;
  label: string;
  hint: string;
  /** Pin / dot colour. Matches CAMPAIGN_STATUS_BADGE: green, blue, grey. */
  color: string;
}[] = [
  {
    status: "ACTIVE",
    label: "Live",
    hint: "Inside both its creative and schedule dates",
    color: "#16a34a",
  },
  {
    status: "UPCOMING",
    label: "Upcoming",
    hint: "Has not started yet",
    color: "#2563eb",
  },
  {
    status: "EXPIRED",
    label: "Expired",
    hint: "Its creative or schedule dates are over",
    color: "#9ca3af",
  },
];

/** Live and upcoming on, expired off: old campaigns should not crowd the first look. */
export const DEFAULT_COVERAGE_STATUSES: CampaignStatus[] = ["ACTIVE", "UPCOMING"];

export const STATUS_COLOR: Record<CampaignStatus, string> = {
  ACTIVE: "#16a34a",
  UPCOMING: "#2563eb",
  EXPIRED: "#9ca3af",
};

const STATUS_RANK: Record<CampaignStatus, number> = {
  ACTIVE: 0,
  UPCOMING: 1,
  EXPIRED: 2,
};

/** The strongest status among a screen's campaigns: live beats upcoming beats expired. */
export const strongestStatus = (campaigns: MapCampaign[]): CampaignStatus =>
  campaigns.reduce<CampaignStatus>(
    (best, c) => (STATUS_RANK[c.status] < STATUS_RANK[best] ? c.status : best),
    "EXPIRED",
  );

export const hasPosition = (
  screen: MapScreen,
): screen is MapScreen & { latitude: number; longitude: number } =>
  typeof screen.latitude === "number" &&
  typeof screen.longitude === "number" &&
  Number.isFinite(screen.latitude) &&
  Number.isFinite(screen.longitude);

/** Narrow every screen to one campaign, dropping screens that do not carry it. */
export const filterByCampaign = (
  screens: MapScreen[],
  creativeId: string,
): MapScreen[] => {
  if (!creativeId) return screens;
  return screens
    .map((screen) => ({
      ...screen,
      campaigns: screen.campaigns.filter((c) => c.creativeId === creativeId),
    }))
    .filter((screen) => screen.campaigns.length > 0);
};

/**
 * Keep only the campaigns (and slots) in the chosen statuses, and drop screens
 * left with nothing.
 *
 * Filtering is per campaign, not per screen: a screen holding one live and one
 * expired campaign, viewed with only Live on, appears with just the live one in
 * its detail — it does not drag the expired one along.
 */
export const filterByStatus = (
  screens: MapScreen[],
  statuses: ReadonlySet<CampaignStatus>,
): MapScreen[] =>
  screens
    .map((screen) => ({
      ...screen,
      campaigns: screen.campaigns
        .filter((c) => statuses.has(c.status))
        .map((c) => ({
          ...c,
          slots: c.slots.filter((slot) => statuses.has(slot.status)),
        })),
    }))
    .filter((screen) => screen.campaigns.length > 0);

/**
 * How many screens carry at least one campaign in each status. A screen with a
 * live and an expired campaign counts under both, which is why the three do not
 * have to add up to the number of screens.
 */
export const countScreensByStatus = (
  screens: MapScreen[],
): Record<CampaignStatus, number> => {
  const counts: Record<CampaignStatus, number> = {
    ACTIVE: 0,
    UPCOMING: 0,
    EXPIRED: 0,
  };
  for (const screen of screens) {
    const present = new Set(screen.campaigns.map((c) => c.status));
    present.forEach((status) => {
      counts[status] += 1;
    });
  }
  return counts;
};

/** Distinct campaigns across a set of screens, for the campaign dropdown. */
export const listCampaigns = (
  screens: MapScreen[],
): { creativeId: string; name: string }[] => {
  const byId = new Map<string, string>();
  for (const screen of screens) {
    for (const campaign of screen.campaigns) {
      if (!byId.has(campaign.creativeId)) {
        byId.set(campaign.creativeId, campaign.name);
      }
    }
  }
  return [...byId.entries()]
    .map(([creativeId, name]) => ({ creativeId, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const UNKNOWN_CITY = "Unknown city";
export const UNKNOWN_REGION = "Unknown area";

export const cityOf = (screen: MapScreen): string =>
  screen.city?.trim() || UNKNOWN_CITY;

export const regionOf = (screen: MapScreen): string =>
  screen.region?.trim() || UNKNOWN_REGION;

/**
 * Where the side panel should go for a group of screens (a clicked cluster):
 * their city if they all share one, and their area too if that is shared as
 * well. Screens spread over several cities give no city, so the panel stays
 * on the city list.
 */
export const panelPathFor = (
  screens: MapScreen[],
): { city: string | null; region: string | null } => {
  const cities = new Set(screens.map(cityOf));
  if (cities.size !== 1) return { city: null, region: null };
  const regions = new Set(screens.map(regionOf));
  return {
    city: [...cities][0],
    region: regions.size === 1 ? [...regions][0] : null,
  };
};

export interface CoverageTotals {
  screenCount: number;
  /** Screens currently showing one of the brand's campaigns. */
  onAir: number;
  campaigns: number;
  plays: number;
  playDuration: number;
}

export interface CoverageRegion extends CoverageTotals {
  region: string;
  screens: MapScreen[];
}

export interface CoverageCity extends CoverageTotals {
  city: string;
  regions: CoverageRegion[];
}

const totalsOf = (screens: MapScreen[]): CoverageTotals => {
  const ids = new Set<string>();
  let onAir = 0;
  let plays = 0;
  let playDuration = 0;

  for (const screen of screens) {
    if (screen.campaigns.some((c) => c.isPlayingNow)) onAir += 1;
    for (const campaign of screen.campaigns) {
      ids.add(campaign.creativeId);
      plays += campaign.plays;
      playDuration += campaign.playDuration;
    }
  }

  return {
    screenCount: screens.length,
    onAir,
    campaigns: ids.size,
    plays,
    playDuration,
  };
};

/** Biggest footprint first, with the "unknown" bucket last however big it is. */
const byFootprint =
  <T extends CoverageTotals>(unknown: string, nameOf: (row: T) => string) =>
  (a: T, b: T) =>
    Number(nameOf(a) === unknown) - Number(nameOf(b) === unknown) ||
    b.screenCount - a.screenCount ||
    nameOf(a).localeCompare(nameOf(b));

const groupBy = <T>(items: T[], keyOf: (item: T) => string) => {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = keyOf(item);
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  }
  return groups;
};

/**
 * The brand's screens as the places they sit in: city, then the areas within
 * it, then the screens themselves.
 *
 * A region belongs to a city in the data model (`regions.cityId`), so this is
 * the one true nesting — city and region are not two ways of slicing the same
 * thing. Every level carries its own totals so a city row can be read without
 * opening it.
 */
export const groupCoverage = (screens: MapScreen[]): CoverageCity[] =>
  [...groupBy(screens, cityOf).entries()]
    .map(([city, cityScreens]) => ({
      city,
      ...totalsOf(cityScreens),
      regions: [...groupBy(cityScreens, regionOf).entries()]
        .map(([region, regionScreens]) => ({
          region,
          ...totalsOf(regionScreens),
          screens: [...regionScreens].sort((a, b) =>
            a.screenName.localeCompare(b.screenName),
          ),
        }))
        .sort(byFootprint(UNKNOWN_REGION, (r) => r.region)),
    }))
    .sort(byFootprint(UNKNOWN_CITY, (c) => c.city));

export interface Cluster<T> {
  lat: number;
  lng: number;
  items: T[];
}

/** Web Mercator world-pixel position of a coordinate at a zoom level. */
const toWorldPixel = (lat: number, lng: number, zoom: number) => {
  const scale = 256 * 2 ** zoom;
  const sin = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
  return {
    x: ((lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale,
  };
};

/**
 * Group points that would land within `cellSize` screen pixels of each other.
 *
 * A grid bucket rather than a distance search: it is O(n), stable while
 * panning, and good enough for a brand's fleet. Zoom in and the same points
 * spread over more pixels, so the buckets split on their own.
 *
 * Points that share exact coordinates never split, however far you zoom — that
 * is every multi-screen venue — so callers must treat a cluster of identical
 * positions as "a place with several screens", not as something to zoom into.
 */
export const clusterPoints = <T extends { latitude: number; longitude: number }>(
  points: T[],
  zoom: number,
  cellSize = 60,
): Cluster<T>[] => {
  const buckets = new Map<string, T[]>();
  for (const point of points) {
    const { x, y } = toWorldPixel(point.latitude, point.longitude, zoom);
    const key = `${Math.floor(x / cellSize)}:${Math.floor(y / cellSize)}`;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(point);
    else buckets.set(key, [point]);
  }

  return [...buckets.values()].map((items) => ({
    lat: items.reduce((sum, p) => sum + p.latitude, 0) / items.length,
    lng: items.reduce((sum, p) => sum + p.longitude, 0) / items.length,
    items,
  }));
};

/** True when every point sits on one coordinate, so zooming can never separate them. */
export const isCoLocated = (points: { latitude: number; longitude: number }[]) =>
  points.every(
    (p) => p.latitude === points[0].latitude && p.longitude === points[0].longitude,
  );
